import { Alert, AlertIcon, Box, Button, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EspdCriterionDto,
  EspdRequirementGroupDto,
  EspdRequirementResponseDto,
  useGetEspdRequestForBidQuery,
  useGetEspdCriteriaQuery,
  useGetEspdResponseQuery,
  useUpdateEspdResponseMutation,
} from "../../../../autogen/bff-api";

// Backend DTO uses String, not an enum, so the codegen emits `string`. The actual
// values are the same as before — the switch-cases below still cover all variants.
type EspdResponseDataType = string;

import { EspdCriterionForm } from "./EspdCriterionForm";

function responseKey(requirementId: string, instanceIndex: number | null | undefined): string {
  return instanceIndex != null ? `${requirementId}_i${instanceIndex}` : requirementId;
}

interface Props {
  previousStep: () => void;
  nextStep: () => void;
  bidId: string;
}

const EXCLUSION_PREFIXES = ["CRITERION.EXCLUSION"];
const SELECTION_PREFIXES = ["CRITERION.SELECTION"];

export const EspdForm = ({ previousStep, nextStep, bidId }: Props) => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language === "no" || i18n.language === "nb";
  const { data: taxonomyData, isLoading: isLoadingTaxonomy } = useGetEspdCriteriaQuery({});
  const { data: requestData, isLoading: isLoadingRequest } = useGetEspdRequestForBidQuery({ bidId });
  const { data: existingResponse, isLoading: isLoadingResponse } = useGetEspdResponseQuery({ bidId });
  const [updateResponse, { isLoading: isSaving }] = useUpdateEspdResponseMutation();

  const [responses, setResponses] = useState<Map<string, EspdRequirementResponseDto>>(new Map());
  const [instanceCounts, setInstanceCounts] = useState<Map<string, number>>(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ criterionId: string; criterionName: string; criterionNameNorwegian?: string; unansweredCount: number }[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const responsesRef = useRef(responses);
  responsesRef.current = responses;

  const selectedCriteria = useMemo(() => {
    if (!taxonomyData?.categories || !requestData?.selectedCriterionIds) return [];
    const selectedIds = new Set(requestData.selectedCriterionIds);
    return taxonomyData.categories
      .flatMap((cat) => cat.criteria)
      .filter((c) => selectedIds.has(c.id));
  }, [taxonomyData, requestData]);

  useEffect(() => {
    if (existingResponse?.responses) {
      const map = new Map<string, EspdRequirementResponseDto>();
      for (const resp of existingResponse.responses) {
        map.set(responseKey(resp.requirementId, resp.instanceIndex), resp);
      }
      setResponses(map);

      // Initialize instance counts from responses for repeatable groups
      if (selectedCriteria.length > 0) {
        const counts = new Map<string, number>();
        for (const criterion of selectedCriteria) {
          for (const group of criterion.requirementGroups) {
            if (group.repeatable) {
              const count = countGroupInstances(group, map);
              counts.set(group.id, count);
            }
          }
        }
        setInstanceCounts(counts);
      }
    }
  }, [existingResponse, selectedCriteria]);

  // Recalculate validation errors when responses change (only if in validation mode)
  useEffect(() => {
    if (isValidating && selectedCriteria.length > 0) {
      const errors = collectUnansweredRequirements(selectedCriteria, responses);
      setValidationErrors(errors);
    }
  }, [responses, selectedCriteria, isValidating]);

  const { exclusionCriteria, selectionCriteria, otherCriteria } = useMemo(() => {
    const exclusion: EspdCriterionDto[] = [];
    const selection: EspdCriterionDto[] = [];
    const other: EspdCriterionDto[] = [];

    for (const criterion of selectedCriteria) {
      if (EXCLUSION_PREFIXES.some((p) => criterion.typeCode.startsWith(p))) {
        exclusion.push(criterion);
      } else if (SELECTION_PREFIXES.some((p) => criterion.typeCode.startsWith(p))) {
        selection.push(criterion);
      } else {
        other.push(criterion);
      }
    }

    return { exclusionCriteria: exclusion, selectionCriteria: selection, otherCriteria: other };
  }, [selectedCriteria]);

  const triggerSave = useCallback(async () => {
    const responseList = Array.from(responsesRef.current.values());
    await updateResponse({
      bidId,
      updateEspdResponseRequest: {
        responses: responseList,
      },
    });
    setHasUnsavedChanges(false);
  }, [bidId, updateResponse]);

  const debouncedSave = useMemo(
    () => debounce(() => triggerSave(), 2000),
    [triggerSave]
  );
  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  const handleResponseChange = useCallback(
    (requirementId: string, value: string | boolean | number | null, instanceIndex?: number | null) => {
      setResponses((prev) => {
        const next = new Map(prev);
        const key = responseKey(requirementId, instanceIndex);
        const existing = next.get(key) ?? { requirementId, instanceIndex: instanceIndex ?? undefined };

        const dataType = findRequirementDataType(selectedCriteria, requirementId);

        const updated = { ...existing };
        switch (dataType) {
          case "INDICATOR":
            updated.indicator = value as boolean;
            break;
          case "DATE":
            updated.date = value as string | null;
            break;
          case "DESCRIPTION":
            updated.description = value as string | null;
            break;
          case "AMOUNT":
            updated.amount = value as number | null;
            break;
          case "CODE":
          case "CODE_COUNTRY":
            updated.code = value as string | null;
            break;
          case "PERCENTAGE":
            updated.percentage = value as number | null;
            break;
          case "PERIOD":
            updated.period = value as string | null;
            break;
          case "QUANTITY":
          case "QUANTITY_INTEGER":
            updated.quantity = value as number | null;
            break;
          case "QUANTITY_YEAR":
            updated.quantity = value as number | null;
            updated.quantityUnitCode = "YEAR";
            break;
          case "EVIDENCE_URL":
            updated.evidenceUrl = value as string | null;
            break;
        }

        next.set(key, updated);
        return next;
      });
      setHasUnsavedChanges(true);
      debouncedSave();
    },
    [selectedCriteria, debouncedSave]
  );

  const handleMetadataChange = useCallback(
    (requirementId: string, field: string, value: string | null, instanceIndex?: number | null) => {
      setResponses((prev) => {
        const next = new Map(prev);
        const key = responseKey(requirementId, instanceIndex);
        const existing = next.get(key) ?? { requirementId, instanceIndex: instanceIndex ?? undefined };
        next.set(key, { ...existing, [field]: value });
        return next;
      });
      setHasUnsavedChanges(true);
      debouncedSave();
    },
    [debouncedSave]
  );

  const handleAddInstance = useCallback(
    (group: EspdRequirementGroupDto) => {
      setInstanceCounts((prev) => {
        const next = new Map(prev);
        const current = next.get(group.id) ?? 1;
        next.set(group.id, current + 1);
        return next;
      });
    },
    []
  );

  const handleRemoveInstance = useCallback(
    (group: EspdRequirementGroupDto, index: number) => {
      // Decrement the instance count
      setInstanceCounts((prev) => {
        const next = new Map(prev);
        const current = next.get(group.id) ?? 1;
        next.set(group.id, Math.max(1, current - 1));
        return next;
      });

      // Re-index responses: shift entries after the removed index down by one
      setResponses((prev) => {
        const next = new Map(prev);
        const allReqIds = collectGroupReqIds(group);
        const instanceCount = countGroupInstances(group, prev);
        for (let i = index; i < instanceCount - 1; i++) {
          for (const reqId of allReqIds) {
            const sourceKey = responseKey(reqId, i + 1);
            const targetKey = responseKey(reqId, i);
            const val = next.get(sourceKey);
            if (val) {
              next.set(targetKey, { ...val, requirementId: reqId, instanceIndex: i });
            } else {
              next.delete(targetKey);
            }
          }
        }
        for (const reqId of allReqIds) {
          next.delete(responseKey(reqId, instanceCount - 1));
        }
        return next;
      });
      setHasUnsavedChanges(true);
      debouncedSave();
    },
    [debouncedSave]
  );

  const handleManualSave = useCallback(async () => {
    debouncedSave.cancel();
    await triggerSave();
  }, [debouncedSave, triggerSave]);

  const handleNext = useCallback(async () => {
    if (hasUnsavedChanges) {
      await debouncedSave.flush();
    }
    const errors = collectUnansweredRequirements(selectedCriteria, responsesRef.current);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setIsValidating(true);
      return;
    }
    setValidationErrors([]);
    setIsValidating(false);
    nextStep();
  }, [hasUnsavedChanges, debouncedSave, selectedCriteria, nextStep]);

  const criteriaWithErrors = useMemo(
    () => new Set(validationErrors.map((e) => e.criterionId)),
    [validationErrors]
  );

  if (isLoadingTaxonomy || isLoadingRequest || isLoadingResponse) {
    return (
      <Flex justifyContent="center" pt="10">
        <Spinner color="teal" />
      </Flex>
    );
  }

  if (selectedCriteria.length === 0) {
    return (
      <Box pt="4" pb="8">
        <Text color="smMuted">{t("espd_no_criteria_selected")}</Text>
        <Flex justifyContent="space-between" mt="6">
          <Button onClick={previousStep} variant="outline" colorScheme="teal" size="sm">
            {t("Previous")}
          </Button>
          <Button onClick={nextStep} colorScheme="teal" size="sm">
            {t("Next")}
          </Button>
        </Flex>
      </Box>
    );
  }

  const renderSection = (title: string, criteria: EspdCriterionDto[]) => {
    if (criteria.length === 0) return null;
    return (
      <Box mb="8">
        <Heading as="h4" size="md" mb="4" color="teal.700" borderBottom="2px solid" borderColor="teal.500" pb="2">
          {title}
        </Heading>
        {criteria.map((criterion) => (
          <EspdCriterionForm
            key={criterion.id}
            criterion={criterion}
            responses={responses}
            instanceCounts={instanceCounts}
            onResponseChange={handleResponseChange}
            onMetadataChange={handleMetadataChange}
            onAddInstance={handleAddInstance}
            onRemoveInstance={handleRemoveInstance}
            hasErrors={criteriaWithErrors.has(criterion.id)}
            isValidating={isValidating}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box pt="4" pb="8">
      <Heading as="h3" size="md" mb="2">
        {t("ESPD — European Single Procurement Document")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("espd_supplier_form_intro")}
      </Text>

      {renderSection(t("Part III — Exclusion Grounds"), exclusionCriteria)}
      {renderSection(t("Part IV — Selection Criteria"), selectionCriteria)}
      {renderSection(t("Other"), otherCriteria)}

      {validationErrors.length > 0 && (
        <Alert status="warning" borderRadius="md" mb="4" alignItems="flex-start">
          <AlertIcon mt="1" />
          <Box>
            <Text fontWeight="medium" mb="1">{t("espd_validation_incomplete")}</Text>
            {validationErrors.map((err) => {
              const displayName = isNorwegian && err.criterionNameNorwegian ? err.criterionNameNorwegian : err.criterionName;
              return (
                <Text key={err.criterionId} fontSize="sm">
                  {displayName} — {t("espd_validation_unanswered_count", { count: err.unansweredCount })}
                </Text>
              );
            })}
          </Box>
        </Alert>
      )}

      <Flex justifyContent="space-between" mt="6">
        <Button onClick={previousStep} variant="outline" colorScheme="teal" size="sm">
          {t("Previous")}
        </Button>
        <Flex gap="2">
          {hasUnsavedChanges && (
            <Button onClick={handleManualSave} isLoading={isSaving} colorScheme="teal" variant="solid" size="sm">
              {t("Save")}
            </Button>
          )}
          <Button onClick={handleNext} colorScheme="teal" size="sm">
            {t("Next")}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

function findRequirementDataType(
  criteria: EspdCriterionDto[],
  requirementId: string
): EspdResponseDataType | undefined {
  for (const criterion of criteria) {
    const found = searchGroups(criterion.requirementGroups, requirementId);
    if (found) return found;
  }
  return undefined;
}

function searchGroups(
  groups: { requirements: { id: string; responseDataType: EspdResponseDataType }[]; subGroups: typeof groups }[],
  requirementId: string
): EspdResponseDataType | undefined {
  for (const group of groups) {
    for (const req of group.requirements) {
      if (req.id === requirementId) return req.responseDataType;
    }
    const found = searchGroups(group.subGroups, requirementId);
    if (found) return found;
  }
  return undefined;
}

function collectUnansweredRequirements(
  criteria: EspdCriterionDto[],
  responses: Map<string, EspdRequirementResponseDto>
): { criterionId: string; criterionName: string; criterionNameNorwegian?: string; unansweredCount: number }[] {
  return criteria.flatMap((criterion) => {
    const count = criterion.requirementGroups.reduce(
      (sum, group) => sum + countUnansweredInGroup(group, responses, undefined),
      0
    );
    return count > 0 ? [{ criterionId: criterion.id, criterionName: criterion.name, criterionNameNorwegian: criterion.nameNorwegian ?? undefined, unansweredCount: count }] : [];
  });
}

function countUnansweredInGroup(
  group: EspdRequirementGroupDto,
  responses: Map<string, EspdRequirementResponseDto>,
  parentIndicatorValue: boolean | null | undefined
): number {
  if (group.processingInstruction) {
    if (group.processingInstruction === "GROUP_FULFILLED.ON_TRUE" && parentIndicatorValue !== true) return 0;
    if (group.processingInstruction === "GROUP_FULFILLED.ON_FALSE" && parentIndicatorValue !== false) return 0;
  }

  if (group.repeatable) {
    const indicatorReq = group.requirements.find((r) => r.responseDataType === "INDICATOR");
    const indicatorKey = indicatorReq ? responseKey(indicatorReq.id, 0) : undefined;
    const indicatorValue = indicatorKey ? (responses.get(indicatorKey)?.indicator ?? null) : null;
    const unanswered = group.requirements.filter(
      (req) => !req.optional && !isRequirementAnswered(req, responses, 0)
    ).length;
    const subUnanswered = group.subGroups.reduce(
      (sum, sub) => sum + countUnansweredInGroup(sub, responses, indicatorValue),
      0
    );
    return unanswered + subUnanswered;
  }

  const indicatorReq = group.requirements.find((r) => r.responseDataType === "INDICATOR");
  const indicatorValue = indicatorReq ? (responses.get(indicatorReq.id)?.indicator ?? null) : null;

  const unansweredInGroup = group.requirements.filter(
    (req) => !req.optional && !isRequirementAnswered(req, responses)
  ).length;

  const unansweredInSubGroups = group.subGroups.reduce(
    (sum, sub) => sum + countUnansweredInGroup(sub, responses, indicatorValue),
    0
  );

  return unansweredInGroup + unansweredInSubGroups;
}

function isRequirementAnswered(
  req: { id: string; responseDataType: string; optional: boolean },
  responses: Map<string, EspdRequirementResponseDto>,
  instanceIndex?: number | null
): boolean {
  const response = responses.get(responseKey(req.id, instanceIndex));
  if (!response) return false;

  switch (req.responseDataType) {
    case "INDICATOR":
      return response.indicator === true || response.indicator === false;
    case "DATE":
      return response.date != null && response.date !== "";
    case "DESCRIPTION":
      return response.description != null && response.description !== "";
    case "AMOUNT":
      return response.amount != null;
    case "CODE":
    case "CODE_COUNTRY":
      return response.code != null && response.code !== "";
    case "PERCENTAGE":
      return response.percentage != null;
    case "PERIOD":
      return response.period != null && response.period !== "";
    case "QUANTITY":
    case "QUANTITY_INTEGER":
    case "QUANTITY_YEAR":
      return response.quantity != null;
    case "EVIDENCE_URL":
      return response.evidenceUrl != null && response.evidenceUrl !== "";
    default:
      return response.description != null && response.description !== "";
  }
}

function collectGroupReqIds(group: EspdRequirementGroupDto): string[] {
  const ids = group.requirements.map((r) => r.id);
  for (const sub of group.subGroups) {
    ids.push(...collectGroupReqIds(sub));
  }
  return ids;
}

function countGroupInstances(
  group: EspdRequirementGroupDto,
  responses: Map<string, EspdRequirementResponseDto>
): number {
  const firstReqId = group.requirements[0]?.id;
  if (!firstReqId) return 1;
  let count = 0;
  while (responses.has(responseKey(firstReqId, count))) count++;
  return Math.max(count, 1);
}

